/**
 * Pure logic backing `components/CustomRangeControl.js` — the mobile-only
 * "jump to an exact date range" control that exists because
 * `rangeSelector.inputEnabled: false` on mobile (see chartConfig.js /
 * ratioChartConfig.js) removed Highcharts' native free-text date inputs,
 * leaving only the fixed "N years back from today" preset buttons. There is
 * now no way on a phone to precisely hit an arbitrary historical window like
 * "2000-01-01 to 2010-12-31" that doesn't end at the most recent datapoint.
 *
 * Deliberately kept free of any Highcharts/React dependency so the date
 * parsing/validation/clamping rules can be unit-tested in isolation from a
 * live chart instance.
 */

/**
 * Parses a `<input type="date">` value ("YYYY-MM-DD") into a UTC midnight
 * timestamp, matching the UTC-ms convention `price`/metric series already
 * use elsewhere in this app (see rangeSelectorConfig.js's computeDefaultRange).
 * Returns `null` for an empty/invalid value rather than throwing, so callers
 * can treat "not a valid date" as just another validation failure.
 */
export const parseDateInput = (value) => {
  if (!value) return null;
  const ts = Date.parse(`${value}T00:00:00.000Z`);
  return Number.isNaN(ts) ? null : ts;
};

/** Formats a UTC timestamp back into a `<input type="date">`-compatible string. */
export const formatDateInput = (ts) => (
  ts == null ? '' : new Date(ts).toISOString().slice(0, 10)
);

/**
 * Reads the full available data span (independent of whatever window is
 * currently zoomed/panned to) off a live Highcharts chart instance.
 * `xAxis.getExtremes()` always exposes `dataMin`/`dataMax` for the whole
 * series regardless of the axis's current `min`/`max`, so no separate
 * "raw price data" plumbing is needed just for this.
 */
export const getDataBounds = (chart) => {
  const axis = chart?.xAxis?.[0];
  if (!axis) return null;
  const { dataMin, dataMax } = axis.getExtremes();
  return (dataMin == null || dataMax == null) ? null : { dataMin, dataMax };
};

/**
 * Validates and clamps a requested [startStr, endStr] custom range against
 * the chart's actual data bounds.
 *
 * Returns either `{ error: string }` (nothing applied — e.g. missing/
 * unparseable dates, start on/after end, or a request that misses the
 * available data entirely) or `{ start, end, clamped }` where `start`/`end`
 * are UTC timestamps ready for `xAxis.setExtremes`, and `clamped` is true
 * when the requested range extended beyond the data and was narrowed to fit
 * (the caller uses this to surface a small "adjusted to available data"
 * message rather than silently doing something the user didn't ask for).
 */
export const resolveCustomRange = (startStr, endStr, bounds) => {
  const startTs = parseDateInput(startStr);
  const endTs = parseDateInput(endStr);

  if (startTs == null || endTs == null) {
    return { error: 'Enter both a start and end date.' };
  }
  if (startTs >= endTs) {
    return { error: 'Start date must be before end date.' };
  }
  if (!bounds) {
    return { error: 'No chart data loaded yet.' };
  }

  const { dataMin, dataMax } = bounds;
  if (endTs < dataMin || startTs > dataMax) {
    return {
      error: `No data in that range — available data runs from ${formatDateInput(dataMin)} to ${formatDateInput(dataMax)}.`,
    };
  }

  const clampedStart = Math.max(startTs, dataMin);
  const clampedEnd = Math.min(endTs, dataMax);

  return {
    start: clampedStart,
    end: clampedEnd,
    clamped: clampedStart !== startTs || clampedEnd !== endTs,
  };
};
